import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IFilter, IRankingStore} from "data/stores/ranking/ranking.store";
import type {IRanking} from "data/providers/api/ranking.api.provider";
import {LeaderboardType} from "data/enums";
import {SelectChangeEvent} from "@mui/material";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import {orderBy} from "lodash";

export interface ILeaderboardTableController extends ViewController {
	readonly i18n: ILocalizationStore;

	get leaderboards(): IRanking[];
	get selectedLeaderboard(): LeaderboardType;
	get isPersonalLeaderboard(): boolean;
	get filter(): IFilter;
	get squads(): ISquad[];

	handlerChange: (event: SelectChangeEvent) => void;
}

@injectable()
export class LeaderboardTableController implements ILeaderboardTableController {
	get leaderboards() {
		return this._rankingStore.list;
	}

	get selectedLeaderboard() {
		return this._rankingStore.selectedLeaderboard;
	}

	get isPersonalLeaderboard() {
		return this.selectedLeaderboard === LeaderboardType.PERSONAL;
	}

	get filter() {
		return this._rankingStore.filter;
	}

	get squads() {
		return orderBy(this._squadsStore.list, "name", "asc");
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RankingStore) public _rankingStore: IRankingStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore
	) {
		makeAutoObservable(this);
	}

	handlerChange = (event: SelectChangeEvent) => {
		const {value, name} = event.target;

		this._rankingStore.setFilter(value, name as keyof IFilter);

		if (this._rankingStore.selectedLeaderboard === LeaderboardType.PERSONAL) {
			void this._rankingStore.fetchPersonalLeaderboards();
			return;
		}

		if (this._rankingStore.selectedLeaderboard === LeaderboardType.FAN) {
			void this._rankingStore.fetchFanLeaderboards();
			return;
		}

		void this._rankingStore.fetchOverallLeaderboards();
	};
}
